
import { QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { EmailProcessorAgentResponseMetadata, OrchestratorAgentResponse } from "./types";

type InvokeAgentFunction = (args: {
  targetAgent: string;
  subIntent: string;
  eventData: Record<string, unknown>;
  priority: 'high' | 'normal' | 'low';
}) => Promise<OrchestratorAgentResponse | undefined>;

export const getProcessEmailsMutationConfig = (
  invokeAgent: InvokeAgentFunction,
  queryClient: QueryClient,
  emailsToProcessPerRequest: number
) => ({
  mutationFn: async () => {
    const response = await invokeAgent({
      targetAgent: 'email-processor-agent',
      subIntent: 'EMAIL_PROCESS_REQUEST',
      eventData: { limit: emailsToProcessPerRequest },
      priority: 'normal',
    });
    console.log('Email processing (A2A) initiated response:', response);
    if (response?.metadata?.status === 'error') {
      throw new Error(response.content || 'Failed to process emails via agent');
    }
    return response;
  },
  onSuccess: (response: OrchestratorAgentResponse | undefined) => {
    queryClient.invalidateQueries({ queryKey: ['processed-emails'] });
    const metadata = response?.metadata as EmailProcessorAgentResponseMetadata | undefined;
    const processedCount = metadata?.processedCount || 0;
    if (processedCount > 0) {
      toast.success(`Successfully processed ${processedCount} email(s). Results will appear shortly.`);
    } else {
      toast.info('No new emails to process at the moment.');
    }
  },
  onError: (error: Error) => {
    console.error("Error initiating email processing via A2A:", error);
    toast.error(`Failed to process emails: ${error.message}`);
  }
});

export const getArchiveEmailMutationConfig = (
  invokeAgent: InvokeAgentFunction,
  queryClient: QueryClient
) => ({
  mutationFn: async (emailId: string) => {
    const response = await invokeAgent({
      targetAgent: 'email-processor-agent',
      subIntent: 'ARCHIVE_EMAIL',
      eventData: { emailId },
      priority: 'normal',
    });
    console.log('Archive email response:', response);
    if (response?.metadata?.status === 'error') {
      throw new Error(response.content || 'Failed to archive email');
    }
    return response;
  },
  onSuccess: (_data: OrchestratorAgentResponse | undefined, emailId: string) => {
    queryClient.invalidateQueries({ queryKey: ['processed-emails'] });
    toast.success(`Email (ID: ${emailId.substring(0,6)}) successfully archived.`);
  },
  onError: (error: Error, emailId: string) => {
    console.error(`Error archiving email ${emailId}:`, error);
    toast.error(`Failed to archive email: ${error.message}`);
  }
});

export const getFlagEmailMutationConfig = (
  invokeAgent: InvokeAgentFunction,
  queryClient: QueryClient
) => ({
  mutationFn: async ({ emailId, currentFlagStatus }: { emailId: string; currentFlagStatus: boolean }) => {
    const response = await invokeAgent({
      targetAgent: 'email-processor-agent',
      subIntent: 'FLAG_EMAIL',
      eventData: { emailId, newFlagStatus: !currentFlagStatus },
      priority: 'normal',
    });
    console.log('Flag email response:', response);
    if (response?.metadata?.status === 'error') {
      throw new Error(response.content || 'Failed to flag email');
    }
    return response;
  },
  onSuccess: (_data: OrchestratorAgentResponse | undefined, { emailId, currentFlagStatus }: { emailId: string; currentFlagStatus: boolean }) => {
    queryClient.invalidateQueries({ queryKey: ['processed-emails'] });
    toast.success(`Email (ID: ${emailId.substring(0,6)}) ${currentFlagStatus ? 'unflagged' : 'flagged'} successfully.`);
  },
  onError: (error: Error, { emailId }: { emailId: string; currentFlagStatus: boolean }) => {
    console.error(`Error flagging email ${emailId}:`, error);
    toast.error(`Failed to update flag status: ${error.message}`);
  }
});
